import {
  Dispatch,
  useState,
} from 'react';

import {
  ActionCenterTypes,
  ContentBlockType,
  FormState,
  MatchedTargets,
  OptInOptions,
  TakeActionErrorsFields,
  TakeActionFormState,
} from '@/core/interfaces/common';
import { AdvocateSignature } from '@/core/components/AdvocateSignature';
import { FormInput } from '@/core/components/FormInput';
import { Greeting } from '@/core/components/Greeting';

import { hasAnyError } from '@/utils/helpers';
import { fieldHelpText } from '@/utils/constants';

import {
  CenteredErrorMessage,
  ErrorMessage,
  FormHeading,
  FormInputWrapper,
  GeneralErrorMessage,
  SendButton,
  StepButton,
  SubjectInputWrapper,
  SubmitButtonWrapper,
  SubmitSection,
  TalkingPointItem,
  TalkingPointsList,
} from '@/themes/globalStyles';

import { ContactFieldsContainer } from '../ContactFieldsContainer';
import { CustomContactFields } from './CustomContactFields';
import { OptCheckboxes } from '../OptCheckboxesSecunda';
import {
  ActionsWrapper,
  EditableTextButton,
  EmailText,
  SignatureArea,
  StyledPickActionType,
  SubjectText,
} from './TakeActionFormContainer.styles';
import { AutoResizingTextarea } from './AutoResizingTextarea';
import { PersonalMessageTextarea } from './PersonalMessageTextarea';

interface TakeActionFormContainerProps {
  actionCenterType: ActionCenterTypes;
  backgroundColor: string;
  buttonColor: string;
  currentMessageIndex: number;
  emailChannel: boolean;
  error: TakeActionErrorsFields;
  formState: TakeActionFormState;
  handleBlurAddress?: (streetAddress?: string, zipCode?: string) => void;
  hideMessageHeadline?: boolean;
  isPending: boolean;
  isDisabledInputText?: boolean;
  mode: string;
  optIn: OptInOptions;
  organizationName: string;
  phoneChannel: boolean;
  previewTargets: MatchedTargets;
  setActionCenterType?: Dispatch<React.SetStateAction<ActionCenterTypes>>;
  setCurrentMessageIndex: Dispatch<React.SetStateAction<number>>;
  setError: Dispatch<React.SetStateAction<TakeActionErrorsFields>>;
  setFormState: Dispatch<React.SetStateAction<TakeActionFormState>>;
  showPickActionButton?: boolean;
  talkingPoints: string;
  textColor: string;
}

export const TakeActionFormContainer = ({
  actionCenterType,
  backgroundColor,
  buttonColor,
  currentMessageIndex,
  emailChannel,
  error,
  formState,
  handleBlurAddress,
  hideMessageHeadline,
  isDisabledInputText,
  isPending,
  mode,
  optIn,
  organizationName,
  phoneChannel,
  previewTargets,
  setActionCenterType,
  setCurrentMessageIndex,
  setError,
  setFormState,
  showPickActionButton,
  talkingPoints,
  textColor,
}: TakeActionFormContainerProps) => {
  const [
    editingBlockIndex,
    setEditingBlockIndex,
  ] = useState<number | null>(null);
  const {
    subjectMessagePairs,
    isOptInEmail,
    isOptInText,
  } = formState;
  const {
    emailType,
    phoneType,
  } = ActionCenterTypes;

  const isPetition = mode === 'petition';

  const handleOptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked,
    } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      [name]: checked,
    }));
  };

  const handleClearSubjectField = () => {
    setError(prevValues => {
      if (!prevValues.messages?.[currentMessageIndex]) {
        return prevValues;
      }

      return {
        ...prevValues,
        messages: prevValues.messages.map((messageError, index) => (
          index === currentMessageIndex ?
            {
              ...messageError,
              subject: [],
            } :
            messageError
        )),
      };
    });
  };

  const handleSetSubjectError = (message: string) => {
    setError(prevValues => {
      const messages = [...(prevValues.messages ?? [])];

      while (messages.length <= currentMessageIndex) {
        messages.push({});
      }

      messages[currentMessageIndex] = {
        ...messages[currentMessageIndex],
        subject: [message],
      };

      return {
        ...prevValues,
        messages,
      };
    });
  };

  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      subjectMessagePairs: prevValues.subjectMessagePairs.map((pair, index) => (
        index === currentMessageIndex ?
          {
            ...pair,
            subject: {
              ...pair.subject,
              name: value,
            },
          } :
          pair
      )),
    }));
  };

  const handleMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
    blockIndex: number
  ) => {
    const { value } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      subjectMessagePairs: prevValues.subjectMessagePairs.map((pair, index) => (
        index === currentMessageIndex ?
          {
            ...pair,
            body: pair.body.map((block, blockIdx) => (
              blockIdx === blockIndex ?
                {
                  ...block,
                  name: value,
                } :
                block
            )),
          } :
          pair
      )),
    }));
  };

  const handleClearMessageField = () => {
    setError(prevValues => {
      if (!prevValues.messages?.[currentMessageIndex]) {
        return prevValues;
      }

      return {
        ...prevValues,
        messages: prevValues.messages.map((messageError, index) => (
          index === currentMessageIndex ?
            {
              ...messageError,
              message: [],
            } :
            messageError
        )),
      };
    });
  };

  const handlePersonalMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      subjectMessagePairs: prevValues.subjectMessagePairs.map((pair, index) => (
        index === currentMessageIndex ?
          {
            ...pair,
            personalMessage: value,
          } :
          pair
      )),
    }));
  };

  const handleTextClick = (index: number) => {
    setEditingBlockIndex(index);
  };

  const renderError = (fieldError: string) => (<ErrorMessage role="alert">{fieldError}</ErrorMessage>);

  const actButtonText = (): string => {
    if (actionCenterType === phoneType) {
      return 'Call';
    }

    if (isPetition) {
      return 'Sign petition';
    }

    return 'Send message';
  };

  const getButtonAriaLabel = (): string => {
    if (isPetition) {
      return 'Sign petition';
    }

    const formatRecipientNames = (): string => {
      if (!previewTargets.length) {
        return 'your matched officials';
      }

      const names = previewTargets
        .map(t => `${t.title || ''} ${t.firstName} ${t.lastName}`.trim())
        .slice(0, 3);

      if (previewTargets.length > 3) {
        return `${names.join(', ')} and ${previewTargets.length - 3} others`;
      }

      return names.join(', ');
    };

    const recipients = formatRecipientNames();

    if (actionCenterType === phoneType) {
      return `Call me and connect me to ${recipients}`;
    }

    return `Send message to ${recipients}`;
  };

  const renderEditableMessageBlock = (messageBlock: ContentBlockType, index: number) => {
    if (editingBlockIndex === index) {
      return (
        <AutoResizingTextarea
          hiddenLabel={`editable block ${index + 1}`}
          backgroundcolor={backgroundColor}
          isDisabled={!messageBlock.isAllowEdit}
          onChange={event => handleMessageChange(event, index)}
          onFocus={handleClearMessageField}
          value={messageBlock.name}
        />
      );
    }

    let ariaLabel = `Message block ${index + 1}`;

    if (messageBlock.isAllowEdit) {
      ariaLabel = `Editable message block ${index + 1}. Press Enter to edit.`;
    }

    return (
      <EditableTextButton
        aria-label={ariaLabel}
        isEditable={messageBlock.isAllowEdit}
        onClick={() => messageBlock.isAllowEdit && handleTextClick(index)}
        type="button"
      >
        {messageBlock.name}
      </EditableTextButton>
    );
  };

  // These fields should be labeled as required, both visually
  // and using field attributes for both usability and for
  // accessibility. Users with screen readers need to know upfront
  // whether a field is required. For petitions, we do NOT need
  // to capture the prefix or the phone number; these are more
  // often than not friction for users who would otherwise just
  // sign their name.
  const requiredFields = {
    custom: true,
    email: true,
    firstName: true,
    lastName: true,
    phoneNumber: !isPetition,
    prefix: !isPetition,
    streetAddress: true,
    zipCode: true,
  };

  return (
    <>
      <ContactFieldsContainer
        backgroundColor={backgroundColor}
        error={error}
        formState={formState}
        handleBlurAddress={handleBlurAddress}
        helpText={{
          phoneNumber: fieldHelpText.phoneNumber,
          prefix: fieldHelpText.prefix,
        }}
        requiredFields={requiredFields}
        setError={setError}
        setFormState={setFormState as Dispatch<React.SetStateAction<FormState>>}
        textColor={textColor}
        CustomContactFieldsComponent={CustomContactFields}
      />
      <ActionsWrapper>
        {emailChannel && phoneChannel && setActionCenterType && showPickActionButton && (
          <StyledPickActionType
            actionCenterType={actionCenterType}
            backgroundColor={buttonColor}
            setActionCenterType={setActionCenterType}
            textColor={textColor}
          />
        )}
      </ActionsWrapper>
      {emailChannel &&
        actionCenterType === emailType &&
        (subjectMessagePairs[currentMessageIndex] ?
          (
            <>
              {!hideMessageHeadline && (
                <FormHeading>Your Message:</FormHeading>
              )}
              <SubjectInputWrapper>
                {(
                  isDisabledInputText &&
                  !subjectMessagePairs[currentMessageIndex]?.subject?.isAllowEdit) ?
                  (
                    <SubjectText>{subjectMessagePairs[currentMessageIndex]?.subject?.name || ''}</SubjectText>
                  ) :
                  (
                    <FormInput
                      aria-label="Message subject"
                      backgroundcolor={backgroundColor}
                      error={() => error?.messages?.[currentMessageIndex]?.subject?.[0] || false}
                      isDisabled={
                        !subjectMessagePairs[currentMessageIndex]?.subject?.isAllowEdit
                      }
                      isRequired
                      label="Subject"
                      name="subject"
                      onChange={handleSubjectChange}
                      onFocus={handleClearSubjectField}
                      onInvalidField={() => handleSetSubjectError('Please add a message subject')}
                      value={subjectMessagePairs[currentMessageIndex]?.subject?.name || ''}
                    />
                  )}
              </SubjectInputWrapper>
              <Greeting
                isEmailAction
                targets={previewTargets}
                mode={mode}
              />
              {subjectMessagePairs[currentMessageIndex]?.isAllowPersonalMessage ?
                (
                  <FormInputWrapper>
                    <PersonalMessageTextarea
                      aria-label="Optional personal message"
                      backgroundcolor={backgroundColor}
                      onChange={handlePersonalMessageChange}
                      value={subjectMessagePairs[currentMessageIndex].personalMessage}
                    />
                  </FormInputWrapper>
                ) :
                null
              }
              {subjectMessagePairs[currentMessageIndex]?.body?.length ?
                subjectMessagePairs[currentMessageIndex].body.map((messageBlock, index) => (
                  <FormInputWrapper
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    role="group"
                    aria-label={messageBlock.isAllowEdit ? 'Click-to-edit message block' : 'Non-editable message block'}
                  >
                    {isDisabledInputText && !messageBlock.isAllowEdit ?
                      (
                        messageBlock.name
                          .split('\n')
                          .map(paragraph => paragraph.trim())
                          .filter(Boolean)
                          .map((paragraph, i) => (
                            <EmailText
                              // eslint-disable-next-line react/no-array-index-key
                              key={`${index}-p-${i}`}
                            >
                              {paragraph}
                            </EmailText>
                          ))
                      ) :
                      renderEditableMessageBlock(messageBlock, index)
                    }
                  </FormInputWrapper>
                )) :
                null
              }
              <AdvocateSignature
                firstName={formState.firstName}
                lastName={formState.lastName}
                mode={mode}
                streetAddress={formState.streetAddress}
                zipCode={formState.zipCode}
                Wrapper={SignatureArea}
              />
              {error?.messages?.[currentMessageIndex] &&
                !!error?.messages?.[currentMessageIndex].message?.length &&
                renderError(error?.messages?.[currentMessageIndex].message?.[0] || '')
              }
            </>
          ) :
          (
            <h5 role="alert">We are sorry but your address does not match any of the officials.</h5>
          ))}
      {phoneChannel && actionCenterType === phoneType && (
        <>
          <FormHeading>Call talking points</FormHeading>
          <TalkingPointsList>
            {talkingPoints.split('\n').map(talkingPoint => (
              <TalkingPointItem
                backgroundcolor={backgroundColor}
                key={talkingPoint}
              >
                {talkingPoint}
              </TalkingPointItem>
            ))}
          </TalkingPointsList>
          <Greeting
            isEmailAction={false}
            targets={previewTargets}
            mode={mode}
          />
        </>
      )}
      <SubmitSection>
        <SubmitButtonWrapper>
          {actionCenterType === emailType &&
            subjectMessagePairs.length > 1 &&
            currentMessageIndex > 0 &&
            subjectMessagePairs.length <= currentMessageIndex + 1 &&
            (
              <StepButton
                backgroundcolor={buttonColor}
                disabled={isPending}
                textcolor={textColor}
                type="button"
                value="Previous"
                onClick={() => setCurrentMessageIndex((prevValue: number) => prevValue - 1)}
              />
            )}
          {actionCenterType === emailType &&
            subjectMessagePairs.length > 1 &&
            subjectMessagePairs.length > currentMessageIndex + 1 &&
            (
              <StepButton
                backgroundcolor={buttonColor}
                disabled={isPending}
                textcolor={textColor}
                type="button"
                value="Next"
                onClick={() => setCurrentMessageIndex((prevValue: number) => prevValue + 1)}
              />
            )}
          {
            (subjectMessagePairs.length === currentMessageIndex + 1 ||
              actionCenterType !== emailType) &&
            (
              <SendButton
                aria-label={getButtonAriaLabel()}
                backgroundcolor={buttonColor}
                disabled={isPending}
                textcolor={textColor}
                type="submit"
                value={actButtonText()}
              />
            )}
        </SubmitButtonWrapper>
        {hasAnyError(error) && (
          <>
            <GeneralErrorMessage role="alert">
              Please see errors above
            </GeneralErrorMessage>
            {
              currentMessageIndex !== 0 &&
              (
                !!error.messages?.[0]?.message?.length ||
                !!error.messages?.[0]?.subject?.length
              ) && (
                <CenteredErrorMessage role="alert">
                  Check the first message - click previous button
                </CenteredErrorMessage>
              )
            }
          </>
        )}
      </SubmitSection>
      <OptCheckboxes
        isOptInEmail={isOptInEmail}
        isOptInText={isOptInText}
        backgroundColor={buttonColor}
        handleOptChange={handleOptChange}
        organizationName={organizationName}
        optIn={optIn}
      />
    </>
  );
};

TakeActionFormContainer.defaultProps = {
  handleBlurAddress: () => null,
  hideMessageHeadline: true,
  isDisabledInputText: false,
  setActionCenterType: () => null,
  showPickActionButton: false,
};
